import BaseMarkdown, {
  MarkdownIt,
  RenderFunction,
  RenderRules,
  renderRules,
} from 'react-native-markdown-display';
import { Text } from '@/components/ui/text';
import { unflatten } from 'flat';
import {
  NutritionPreview,
  NutritionPreviewProps,
} from '~/meal/components/nutrition-preview/NutritionPreview';

export interface MarkdownProps {
  children?: string | null;
}

const customNodes = ['nutrition'] as const;

export const createCustomBlockPlugin = (name: string) => (md: MarkdownIt) => {
  md.inline.ruler.before('emphasis', 'energy_macros', (state, silent) => {
    const start = state.pos;
    const src = state.src.slice(start);

    const regex = new RegExp(`^\\s*::${name}\\[(.+?)]`);
    const match = src.match(regex);
    if (!match) return false;
    if (silent) return true;

    const fullMatch = match[0];
    const paramsStr = match[1];

    const token = state.push(name, '', 0);
    token.content = '';
    token.meta = unflatten(
      Object.fromEntries(new URLSearchParams(paramsStr).entries()),
    );
    state.pos += fullMatch.length;
    return true;
  });
};

const markdownItInstance = customNodes.reduce(
  (markdownIt, name) => markdownIt.use(createCustomBlockPlugin(name)),
  MarkdownIt({ typographer: true }),
);

const rules: RenderRules &
  Record<(typeof customNodes)[number], RenderFunction> = {
  ...renderRules,
  text: (node, children, parent, styles, inheritedStyles = {}) => (
    <Text key={node.key} style={[inheritedStyles, styles.text]}>
      {node.content}
    </Text>
  ),
  inline: (node, children, parent, styles) => (
    <Text key={node.key} style={styles.inline}>
      {children}
    </Text>
  ),
  span: (node, children, parent, styles) => (
    <Text key={node.key} style={styles.span}>
      {children}
    </Text>
  ),
  strong: (node, children, parent, styles) => (
    <Text key={node.key} style={styles.strong}>
      {children}
    </Text>
  ),
  em: (node, children, parent, styles) => (
    <Text key={node.key} style={styles.em}>
      {children}
    </Text>
  ),
  s: (node, children, parent, styles) => (
    <Text key={node.key} style={styles.s}>
      {children}
    </Text>
  ),
  nutrition: (node, children, parent, styles) => {
    const props =
      'sourceMeta' in node ? (node.sourceMeta as NutritionPreviewProps) : null;
    if (!props) {
      console.error(`No meta in ${node.key}`);
      return null;
    }
    return <NutritionPreview {...props} key={node.key} />;
  },
};

export const Markdown = ({ children }: MarkdownProps) => {
  return (
    <BaseMarkdown rules={rules} markdownit={markdownItInstance}>
      {children}
    </BaseMarkdown>
  );
};
